import axios from "axios";
import Cookies from "js-cookie";
import store from "@/store";
import router from "@/router";
import { Message } from "element-ui";

// 存储请求状态的 Map
const pendingMap = new Map();

// ========== 新增：刷新锁，确保刷新仅执行一次 ==========
let isRefreshing = false;

/**
 * 生成请求的唯一标识 (URL + Method + Params + Data)
 * @param {Object} config
 */
const getRequestKey = (config) => {
  let { url, method, params, data } = config;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (e) {
      // data 不是 JSON 字符串则保持原样
    }
  }
  return [url, method, JSON.stringify(params), JSON.stringify(data)].join("&");
};

/**
 * 添加请求锁
 * @param {Object} config
 */
const addRequestLock = (config) => {
  const key = getRequestKey(config);
  // 设置一个 1.5 秒后自动删除该 key 的定时器
  // 这意味着 1.5 秒内，同样的 key 会一直存在于 map 中
  pendingMap.set(key, true);
  setTimeout(() => {
    pendingMap.delete(key);
  }, 1500);
};

/**
 * 检查是否被锁定
 * @param {Object} config
 * @returns {boolean}
 */
const isRequestLocked = (config) => {
  const key = getRequestKey(config);
  return pendingMap.has(key);
};

// ==========================================
// 2. 基础配置
// ==========================================
// const baseURL = "http://192.168.10.178:17913";
// const baseURL = "http://127.0.0.1:17913";
// const baseURL = "https://www.lianlaoban.cn/websiteTest";
const baseURL = "https://www.lianlaoban.cn/website";

const service = axios.create({
  baseURL,
  timeout: 10000,
});

// ==========================================
// 3. 辅助函数
// ==========================================
const handleLogout = () => {
  if (isRefreshing) return; // 已在刷新中，直接返回
  isRefreshing = true; // 标记为正在刷新

  const keysToRemove = ["token", "user", "wuId", "cartNum"];
  keysToRemove.forEach((key) => Cookies.remove(key));
  // 修正：在 .js 文件中不能使用 this.$store，必须使用导入的 store 实例
  store.commit('getUser', null);
  store.commit('getUserId', '');
  store.dispatch("setShowLogin", true);
  const currentPath = router.currentRoute.path.toLowerCase();
  if (currentPath.includes("member")) {
    router.push({ path: '/' });
  }

  setTimeout(() => {
    window.location.reload();
    // 刷新后页面会重新加载，isRefreshing 会自动重置为 false，不影响后续使用
  }, 300);
};

// ==========================================
// 4. 请求拦截器
// ==========================================
service.interceptors.request.use(
  (config) => {
    // --- 节流逻辑 (1.5秒内仅1次) ---
    if (config.method === 'post') {
      if (isRequestLocked(config)) {
        // 如果被锁定，创建 CancelToken 取消本次请求
        const source = axios.CancelToken.source();
        config.cancelToken = source.token;
        // 这里的提示语 'Duplicate request' 用于在响应拦截器中识别
        source.cancel('Duplicate request');
      } else {
        // 如果没被锁定，添加锁定（1.5秒后自动解锁）
        addRequestLock(config);
      }
    }
    // ----------------------------

    const token = Cookies.get("token");
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  }
);

// ==========================================
// 5. 响应拦截器
// ==========================================
service.interceptors.response.use(
  (response) => {
    const res = response.data;
    if (res.code == 1) {
      return res;
    }
    // 处理业务逻辑错误
    if (res.code == "401" || res.code == 401) {
      Message.error(res.message || "The login session has expired. Please log in again");
      handleLogout();
      return Promise.reject(new Error(res.message || "Error"));
    }

    if (res.code == "500" || res.code == 500) {
      Message.error(res.message || "Internal server error");
      return Promise.reject(new Error(res.message || "Error"));
    }

    Message.error(res.message || "Unknown error");
    return Promise.reject(new Error(res.message || "Error"));
  },
  (error) => {
    // --- 特殊处理：如果是被节流取消的请求，静默处理，不报错 ---
    if (axios.isCancel(error)) {
      console.log("请求过于频繁，已自动忽略: " + error.message);
      // 返回一个永远 pending 的 Promise，这样前端页面不会收到报错，也不会触发 .catch
      return new Promise(() => {});
    }

    console.error("Response Error:", error);
    let message = "Unknown system error";

    // 处理 HTTP 状态码错误
    if (error.response) {
      const status = error.response.status;
      switch (status) {
        case 401:
          message = "Unauthorized, please log in";
          handleLogout();
          break;
        case 403:
          message = "Access denied";
          break;
        case 404:
          message = "Request address error";
          break;
        case 500:
          message = "Internal server error";
          break;
        default:
          message = error.message;
      }
    } else if (error.message.includes("timeout")) {
      message = "Request timeout, please check the network";
    } else if (error.message.includes("Network Error")) {
      message = "Network connection error";
    }

    Message.error(message);
    return Promise.reject(error);
  }
);

export default service;
